import { createContext, useContext, useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AppContext = createContext();

export const useApp = () => useContext(AppContext);

export const AppProvider = ({ children }) => {
  const [cartId, setCartId] = useState(() => localStorage.getItem("cartId"));
  const [cart, setCart] = useState({ items: [] });
  const [categories, setCategories] = useState([]);
  const [settings, setSettings] = useState({ whatsapp_number: "919441114344" });
  const [isLoading, setIsLoading] = useState(false);
  const cartIdRef = useRef(cartId);

  // Keep ref in sync
  useEffect(() => {
    cartIdRef.current = cartId;
  }, [cartId]);

  // Initialize cart and return the cart ID
  const ensureCart = useCallback(async () => {
    if (cartIdRef.current) return cartIdRef.current;
    try {
      const response = await axios.post(`${API}/cart`);
      const newCartId = response.data.id;
      localStorage.setItem("cartId", newCartId);
      setCartId(newCartId);
      cartIdRef.current = newCartId;
      return newCartId;
    } catch (error) {
      console.error("Failed to create cart:", error);
      return null;
    }
  }, []);

  // Fetch cart
  const fetchCart = useCallback(async (overrideId) => {
    const id = overrideId || cartIdRef.current;
    if (!id) return;
    try {
      const response = await axios.get(`${API}/cart/${id}`);
      setCart(response.data);
    } catch (error) {
      console.error("Failed to fetch cart:", error);
      localStorage.removeItem("cartId");
      setCartId(null);
      cartIdRef.current = null;
    }
  }, []);

  // Fetch categories
  const fetchCategories = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/categories`);
      setCategories(response.data);
    } catch (error) {
      console.error("Failed to fetch categories:", error);
    }
  }, []);

  // Fetch settings
  const fetchSettings = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/settings`);
      setSettings(response.data);
    } catch (error) {
      console.error("Failed to fetch settings:", error);
    }
  }, []);

  // Add to cart
  const addToCart = async (productId, quantity = 1) => {
    const currentCartId = await ensureCart();
    if (!currentCartId) return false;
    try {
      await axios.post(`${API}/cart/${currentCartId}/items`, { product_id: productId, quantity });
      await fetchCart(currentCartId);
      return true;
    } catch (error) {
      console.error("Failed to add to cart:", error);
      return false;
    }
  };

  // Update cart item
  const updateCartItem = async (productId, quantity) => {
    const currentCartId = cartIdRef.current;
    if (!currentCartId) return false;
    try {
      await axios.put(`${API}/cart/${currentCartId}/items/${productId}`, { quantity });
      await fetchCart(currentCartId);
      return true;
    } catch (error) {
      console.error("Failed to update cart:", error);
      return false;
    }
  };

  // Remove from cart
  const removeFromCart = async (productId) => {
    const currentCartId = cartIdRef.current;
    if (!currentCartId) return false;
    try {
      await axios.delete(`${API}/cart/${currentCartId}/items/${productId}`);
      await fetchCart(currentCartId);
      return true;
    } catch (error) {
      console.error("Failed to remove from cart:", error);
      return false;
    }
  };

  // Clear cart
  const clearLocalCart = () => {
    localStorage.removeItem("cartId");
    setCartId(null);
    cartIdRef.current = null;
    setCart({ items: [] });
  };

  const cartCount = cart.items?.reduce((sum, item) => sum + item.quantity, 0) || 0;
  const cartTotal = cart.items?.reduce((sum, item) => sum + (item.product?.price || 0) * item.quantity, 0) || 0;

  useEffect(() => {
    ensureCart();
    fetchCategories();
    fetchSettings();
  }, [ensureCart, fetchCategories, fetchSettings]);

  useEffect(() => {
    if (cartId) {
      fetchCart(cartId);
    }
  }, [cartId, fetchCart]);

  const value = {
    cart, cartId, cartCount, cartTotal, categories, settings, isLoading, setIsLoading,
    addToCart, updateCartItem, removeFromCart, fetchCart, clearLocalCart,
    API, BACKEND_URL,
  };

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
};

export default AppContext;
